/**
 * Reading the API's plan tiers (`PlanTierOffer[]`) and plan features through guards rather than a
 * cast. Shared by the anonymous signup lookup and the public pricing page, so it imports types only.
 *
 * Web and the API deploy separately: an older API sends neither field, which reads as an empty list,
 * and a plan this build cannot read (no key, no name, no price above zero, an unknown sport) is
 * dropped rather than offered at "$0".
 */

import type { PlanFeature, PlanTierOffer } from '../types/planTiers';

type Rec = Record<string, unknown>;

const asRecord = (value: unknown): Rec | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Rec) : null;

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * A price above zero in whole cents, or null. Used for optional prices (the 3-year price) where
 * null or absent means "not offered".
 */
export const positiveCents = (value: unknown): number | null => {
  const cents = finite(value);
  return cents !== null && cents > 0 ? Math.round(cents) : null;
};

/** A discount percent above zero, or null (not offered, or an older API). */
export const positivePercent = (value: unknown): number | null => {
  const percent = finite(value);
  return percent !== null && percent > 0 ? percent : null;
};

const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A real calendar day as "yyyy-MM-dd", or null (absent, an older API, or not a date). Used for the
 * 3-year agreement's last day, which is a plain date, never an instant.
 */
export const plainDate = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = PLAIN_DATE.exec(value);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const check = new Date(Date.UTC(year, month - 1, day));
  return check.getUTCFullYear() === year && check.getUTCMonth() === month - 1 && check.getUTCDate() === day
    ? value
    : null;
};

/** Each feature with a key and a name, once per key, in the order sent. */
export const parsePlanFeatures = (value: unknown): PlanFeature[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const features: PlanFeature[] = [];
  for (const item of value) {
    const row = asRecord(item);
    const key = text(row?.key);
    const name = text(row?.name);
    if (!row || !key || !name || seen.has(key)) continue;
    seen.add(key);
    features.push({ key, name, description: text(row.description) ?? '' });
  }
  return features;
};

const toOffer = (value: unknown): PlanTierOffer | null => {
  const row = asRecord(value);
  if (!row) return null;
  const key = text(row.key);
  const name = text(row.name);
  const price = finite(row.annualPriceCents);
  const sport = row.sport === 'baseball' || row.sport === 'softball' ? row.sport : null;
  if (!key || !name || !sport || price === null || price <= 0) return null;
  const threeYear = positiveCents(row.threeYearAnnualPriceCents);
  return {
    key,
    name,
    description: text(row.description),
    sport,
    annualPriceCents: Math.round(price),
    // Only carried when offered, so a plan from an older API reads exactly as it did before.
    ...(threeYear !== null ? { threeYearAnnualPriceCents: threeYear } : {}),
    features: parsePlanFeatures(row.features),
    sortOrder: finite(row.sortOrder) ?? 0,
  };
};

/** Readable plans, once per key, in display order (the server's own order breaks ties). */
export const parsePlanTierOffers = (value: unknown): PlanTierOffer[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const offers: PlanTierOffer[] = [];
  for (const item of value) {
    const offer = toOffer(item);
    if (!offer || seen.has(offer.key)) continue;
    seen.add(offer.key);
    offers.push(offer);
  }
  // Array.prototype.sort is stable, so equal sort orders keep the order the server sent.
  return offers.sort((a, b) => a.sortOrder - b.sortOrder);
};

/** The plans offered for one sport, in display order. */
export const plansForSport = (
  plans: readonly PlanTierOffer[] | undefined,
  sport: string | undefined
): PlanTierOffer[] => (plans ?? []).filter((plan) => plan.sport === sport);
